import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import {
  OGI_ADMIN_ROLE_LIST,
  Role,
  SUPER_ADMIN_ROLE_LIST,
} from 'src/app/_models/role';
import { User, UserUpdate } from 'src/app/_models/user';
import {
  UserDeleteResponse,
  UserUpdateResponse,
} from 'src/app/_models/api-responses';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ModalService } from 'src/app/_services/modal.service';
import { UserService } from 'src/app/_services/user.service';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { OrganisationService } from 'src/app/_services/organisation.service';
import { OrganisationIndexView } from 'src/app/_models/organisation';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { LabCardComponent } from 'src/app/_components/cards/lab-card/lab-card.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { ButtonCardComponent } from 'src/app/_components/cards/button-card/button-card.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-update-user',
  templateUrl: './update-user.component.html',
  styleUrl: './update-user.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    ModalComponent,
    ValidationModalComponent,
    RoleCheckPipe,
    ErrorPageComponent,
    LabCardComponent,
    MatIconModule,
    MatSlideToggleModule,
    ToastComponent,
    ButtonCardComponent,
    LoadingSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateUserComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly modalService = inject(ModalService);
  private readonly organisationService = inject(OrganisationService);

  private readonly unsubscribe$ = new Subject<void>();
  private readonly allowNavigation = new Subject<boolean>();

  laboratory: Laboratory;
  orgId: number;
  organisation: OrganisationIndexView | null = null;
  currentUser: User;
  userId: string;

  readonly Role = Role;
  readonly superAdminRoles = SUPER_ADMIN_ROLE_LIST;

  isLoading = signal(true);
  updateUserDetailsProcessing = signal(false);
  updateLabsProcessing = signal(false);
  reset2FAProcessing = signal(false);
  error = signal<ErrorObject | null>(null);
  getUserError = signal<ErrorObject | null>(null);
  user = signal<User | null>(null);
  availableLabs = signal<Laboratory[]>([]);
  labsModified = signal(false);
  labSelectError = signal<string | null>(null);
  updateUserSuccessMessage = signal<string | null>(null);
  unlockAccountProcessing = signal(false);
  disableAccountProcessing = signal(false);

  displayAccountToast = signal(false);
  accountToastMessage = signal('');

  private accountToastTimer?: ReturnType<typeof setTimeout>;

  firstNameField = new FormBase({
    key: 'firstName',
    label: 'First Name',
    type: 'text',
    placeholder: 'First name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  lastNameField = new FormBase({
    key: 'lastName',
    label: 'Last Name',
    type: 'text',
    placeholder: 'Last name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  userRoleField = new FormBase({
    key: 'role',
    label: 'Role',
    type: 'select',
    placeholder: 'Select role',
    disabled: false,
    required: true,
    value: '',
    options: [
      { key: Role.Read_Access_User, value: 'Read Access User' },
      { key: Role.Run_Access_User, value: 'Run Access User' },
      { key: Role.Edit_Access_User, value: 'Edit Access User' },
      { key: Role.Lab_Administrator, value: 'Lab Administrator' },
    ],
  });

  availableLabsField = new FormBase({
    key: 'lab',
    label: 'User Laboratories',
    type: 'select',
    placeholder: 'Select lab',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  protected readonly disableAccountKey = 'disableAccount';

  disableAccountForm = this.formBuilder.group({
    disableAccount: [false],
  });

  editUserForm = this.formBuilder.group({
    [this.firstNameField.key]: ['', [Validators.required]],
    [this.lastNameField.key]: ['', [Validators.required]],
    [this.userRoleField.key]: ['', [Validators.required]],
    [this.availableLabsField.key]: [],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });

    this.setAvailableUserRoles(this.currentUser.role);

    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      this.userId = params.id as string;
      this.getUserDetails(Number.parseInt(this.userId));
    });

    if (this.currentUser.role === Role.Super_User) {
      this.f.firstName.disable();
      this.f.lastName.disable();
    }

    this.laboratory = this.authenticationService.selectedLaboratory;
    this.orgId = this.authenticationService.currentOrganisation;

    this.disableAccountForm.controls.disableAccount.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isDisabled) => {
        if (isDisabled) {
          this.disableAccountForm.controls.disableAccount.setValue(false, {
            emitEvent: false,
          });
          this.openModal('disable-user');
        } else {
          this.disableAccountForm.controls.disableAccount.setValue(true, {
            emitEvent: false,
          });
          this.openModal('re-enable-user');
        }
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  getOrganisation(): void {
    this.organisationService.getOrganisation(this.orgId).subscribe({
      next: (response: OrganisationIndexView) => {
        this.organisation = response;
        this.availableLabs.set(
          response.labSummary.filter((lab) => !lab.isDeleted) || [],
        );
        this.setAvailableLabs(false);
        this.isLoading.set(false);
      },
      error: (err: ErrorObject) => {
        this.isLoading.set(false);
        this.error.set(err);
        this.openModal('error');
      },
    });
  }

  setAvailableUserRoles(role: Role): void {
    if (OGI_ADMIN_ROLE_LIST.includes(role)) {
      this.userRoleField.options.push({
        key: Role.OGI_Laboratory_Administrator,
        value: 'OGI Laboratory Administrator',
      });
    }

    if (role === Role.OGI_Super_Administrator) {
      this.userRoleField.options.push(
        { key: Role.OGI_Super_Administrator, value: 'OGI Super Administrator' },
        { key: Role.Super_User, value: 'Super User' },
      );
    }
  }

  getUserDetails(userId: number): void {
    this.getUserError.set(null);
    this.isLoading.set(true);
    this.userService.getUserDetails(userId).subscribe({
      next: (response) => {
        this.user.set(response);
        this.updateFormValues(response);

        if (this.currentUser.organisationId === this.orgId) {
          this.availableLabs.set(this.currentUser.laboratories || []);
          this.setAvailableLabs(true);
          this.organisationService
            .getOrganisation(this.orgId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: (org) => {
                this.organisation = org;
                this.isLoading.set(false);
              },
              error: (err: ErrorObject) => {
                this.isLoading.set(false);
                this.error.set(err);
                this.openModal('error');
              },
            });
        } else {
          this.getOrganisation();
        }
      },
      error: (err: ErrorObject) => {
        this.isLoading.set(false);
        this.getUserError.set(err);
      },
    });
  }

  setAvailableLabs(ownOrg: boolean): void {
    this.availableLabsField.options = (
      ownOrg ? this.currentUser.laboratories : this.availableLabs()
    ).map((lab) => ({ key: lab.laboratoryId, value: lab.laboratoryName }));

    this.availableLabsField.options = this.availableLabsField.options.filter(
      (option) =>
        !this.user().laboratories.some(
          (lab) => lab.laboratoryId === option.key,
        ),
    );

    this.editUserForm
      .get('lab')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((labId) => {
        const lab = this.availableLabs().find(
          (l) => l.laboratoryId === Number.parseInt(labId),
        );
        if (
          lab &&
          !this.user().laboratories.some(
            (l) => l.laboratoryId === lab.laboratoryId,
          )
        ) {
          this.user.update((u) => ({
            ...u,
            laboratories: [...u.laboratories, lab],
          }));
          this.availableLabsField.options =
            this.availableLabsField.options.filter(
              (option) => option.key !== lab.laboratoryId,
            );
          this.editUserForm.get('lab').setValue(null, { emitEvent: false });
          this.editUserForm.markAsDirty();
          this.labsModified.set(true);
        }
      });
  }

  removeLab(lab: Laboratory): void {
    this.user.update((u) => ({
      ...u,
      laboratories: u.laboratories.filter(
        (l) => l.laboratoryId !== lab.laboratoryId,
      ),
    }));
    this.availableLabsField.options = [
      ...this.availableLabsField.options,
      { key: lab.laboratoryId, value: lab.laboratoryName },
    ].sort((a, b) => a.value.localeCompare(b.value));
    this.editUserForm.get('lab').setValue(null, { emitEvent: false });
    this.editUserForm.markAsDirty();
    this.labsModified.set(true);
  }

  updateFormValues(user: User): void {
    this.editUserForm.patchValue({
      [this.firstNameField.key]: user.firstName,
      [this.lastNameField.key]: user.lastName,
      [this.userRoleField.key]: user.role,
    });
    this.editUserForm.markAsPristine();
    this.labsModified.set(false);

    this.disableAccountForm.controls.disableAccount.setValue(
      user.isDisabled ?? false,
      { emitEvent: false },
    );
  }

  get f(): FormGroup['controls'] {
    return this.editUserForm.controls;
  }

  get detailsModified(): boolean {
    return this.f.firstName.dirty || this.f.lastName.dirty || this.f.role.dirty;
  }

  onSubmit(updateLabs: boolean): void {
    if (updateLabs) {
      this.updateLabsProcessing.set(true);
    } else {
      this.updateUserDetailsProcessing.set(true);
    }

    this.error.set(null);

    const userUpdate: UserUpdate = { id: this.user().id };

    if (updateLabs) {
      userUpdate.laboratoryIds = this.user().laboratories.map(
        (lab) => lab.laboratoryId,
      );
    } else {
      if (this.f.firstName.value !== this.user().firstName) {
        userUpdate.firstName = this.f.firstName.value as string;
      }
      if (this.f.lastName.value !== this.user().lastName) {
        userUpdate.lastName = this.f.lastName.value as string;
      }
      if (this.f.role.value !== this.user().role) {
        userUpdate.userRole = this.f.role.value as Role;
      }
    }

    this.userService.updateUser(userUpdate).subscribe({
      next: (data: UserUpdateResponse) => {
        if (updateLabs) {
          this.updateLabsProcessing.set(false);
        } else {
          this.updateUserDetailsProcessing.set(false);
        }
        this.error.set(null);
        this.updateUserSuccessMessage.set(data.message);
        if (data.user) {
          this.user.set({
            ...data.user,
            laboratories: updateLabs
              ? data.user.laboratories
              : this.user().laboratories,
          });
          this.updateFormValues(this.user());
        }
        this.openModal('success');
      },
      error: (err: ErrorObject) => {
        this.error.set(err);
        this.openModal('error');
        if (updateLabs) {
          this.updateLabsProcessing.set(false);
        } else {
          this.updateUserDetailsProcessing.set(false);
        }
      },
    });
  }

  deleteUser(): void {
    this.error.set(null);
    this.userService.deleteUser(this.user().id).subscribe({
      next: (data: UserDeleteResponse) => {
        this.closeModal('delete-user');
        this.updateUserSuccessMessage.set(data.message);
        this.openModal('success');
      },
      error: (err: ErrorObject) => {
        this.error.set(err);
        this.closeModal('delete-user');
        this.openModal('error');
      },
    });
  }

  disableAccount(isDisabled: boolean): void {
    this.disableAccountProcessing.set(true);
    this.error.set(null);

    this.userService
      .disableAccount(this.user().id, isDisabled)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.disableAccountProcessing.set(false);
          this.user.update((u) => ({ ...u, isDisabled }));
          this.disableAccountForm.controls.disableAccount.setValue(isDisabled, {
            emitEvent: false,
          });
          if (isDisabled) {
            this.closeModal('disable-user');
          } else {
            this.closeModal('re-enable-user');
          }
          this.showAccountToast('Account status updated successfully');
        },
        error: (err: ErrorObject) => {
          this.disableAccountProcessing.set(false);
          this.error.set(err);
          if (isDisabled) {
            this.closeModal('disable-user');
          } else {
            this.closeModal('re-enable-user');
          }
          this.openModal('error');
        },
      });
  }

  unlockAccount(): void {
    this.unlockAccountProcessing.set(true);
    this.error.set(null);

    this.userService
      .unlockAccount(this.user().id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.unlockAccountProcessing.set(false);
          this.user.update((u) => ({ ...u, isLockedOut: false }));
          this.showAccountToast('Account unlocked');
        },
        error: (err: ErrorObject) => {
          this.unlockAccountProcessing.set(false);
          this.error.set(err);
          this.openModal('error');
        },
      });
  }

  resetTwoFactorAuthentication(): void {
    if (this.reset2FAProcessing()) return;

    this.reset2FAProcessing.set(true);
    this.error.set(null);

    this.userService
      .adminResetTwoFactor(this.user().id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
      next: () => {
        this.reset2FAProcessing.set(false);
        this.closeModal('reset-2fa');
        this.showAccountToast('Two-factor authentication reset');
      },
      error: (err: ErrorObject) => {
        this.reset2FAProcessing.set(false);
        this.error.set(err);
        this.closeModal('reset-2fa');
        this.openModal('error');
      },
    });
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'update-user-button':
        this.closeModal('success');
        break;
      case 'error-button':
        this.closeModal('error');
        break;
      case 're-enable-confirm':
        if (!this.disableAccountProcessing()) {
          this.disableAccount(false);
        }
        break;
      case 're-enable-cancel':
        this.closeModal('re-enable-user');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.accountToastTimer) {
      clearTimeout(this.accountToastTimer);
    }
  }

  private showAccountToast(message: string): void {
    if (this.accountToastTimer) {
      clearTimeout(this.accountToastTimer);
    }
    this.accountToastMessage.set(message);
    this.displayAccountToast.set(true);
    this.accountToastTimer = setTimeout(() => {
      this.displayAccountToast.set(false);
    }, 5000);
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.editUserForm.pristine) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
